import React, { useState, useEffect, useCallback } from 'react';
import {
  rankWith,
  isStringControl,
  isNumberControl,
  isIntegerControl,
  ControlProps,
  and,
  schemaMatches,
} from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { TextField } from '@mui/material';
import { FieldLabel } from '../FieldLabel';
import { ContentViewAdornment, type ContentFormatOption } from '../ContentViewAdornment';
import { formatNumber, parseLocaleNumber } from '@/lib/locale';

/**
 * Simple text/number control that renders a MUI TextField with
 * description tooltip on the label. Replaces the default Material
 * renderers for these basic types.
 *
 * Does NOT match:
 * - enum fields (schema.enum / oneOf) — handled by Material enum renderer (priority 5)
 * - entity-lookup fields — handled by LookupRenderer (priority 10)
 * - date/datetime fields — handled by DateRenderer/DateTimeRenderer (priority 15)
 * - dynamic enum fields — handled by EnumRenderer (priority 20)
 */
const TooltipTextField = (props: ControlProps) => {
  const { data, handleChange, path, label, description, required, errors, enabled, visible, schema, uischema } = props;

  // Determine input type from schema
  const schemaType = Array.isArray(schema?.type)
    ? schema.type.find((t: string) => t !== 'null') || 'string'
    : schema?.type || 'string';
  const isNumeric = schemaType === 'number' || schemaType === 'integer';

  // Multiline when the form asks for it (a Text field, or DSL `multiline`/`rows`) or for genuinely
  // long text fields (descriptions, notes) — not for names/titles/paths
  const options = uischema?.options ?? {};
  const isMultiline =
    options.multi === true || (schema as any)?.maxLength > 1000 || schema?.format === 'textarea';
  // DSL `ui.contentFormat`: the field holds a document, viewed formatted in a side panel.
  const contentFormat = options.contentFormat as ContentFormatOption | undefined;

  // Render a numeric value as an editing string in the active locale (decimal separator only,
  // no grouping — grouping while typing fights the caret).
  const toEditString = (v: any): string =>
    typeof v === 'number' && Number.isFinite(v)
      ? formatNumber(v, { useGrouping: false, maximumFractionDigits: schemaType === 'integer' ? 0 : 20 })
      : (v ?? '');

  // Local state prevents character loss during fast typing.
  // JSON Forms store updates are async; using `data` directly as value
  // causes the input to briefly revert to stale values mid-keystroke.
  const [localValue, setLocalValue] = useState<any>(() => (isNumeric ? toEditString(data) : (data ?? '')));

  useEffect(() => {
    if (isNumeric) {
      // Only adopt genuine external changes; ignore echoes of our own edits so an in-progress
      // separator (e.g. a trailing "," in sv) is not stripped mid-typing.
      if (parseLocaleNumber(localValue) !== data) setLocalValue(toEditString(data));
    } else {
      setLocalValue(data ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const applyValue = useCallback((val: string) => {
    setLocalValue(val);
    if (val.trim() === '') {
      handleChange(path, undefined);
    } else if (isNumeric) {
      // Parse via the active format locale so the user can type their decimal separator
      // regardless of the browser locale (e.g. "1,25" in sv).
      const num = parseLocaleNumber(val);
      handleChange(path, Number.isNaN(num) ? undefined : (schemaType === 'integer' ? Math.trunc(num) : num));
    } else {
      handleChange(path, val);
    }
  }, [handleChange, path, isNumeric, schemaType]);

  const handleValueChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => applyValue(e.target.value),
    [applyValue],
  );

  // Read-only number fields (e.g. computed totals) are display-only: render the value formatted
  // via the active locale (grouping + decimal separator). Integers get 0 decimals, other numbers
  // 2 — matching the grid columns. Numeric inputs use a text field (with a decimal input mode) so
  // the locale decimal separator can be typed and rendered; non-numeric fields keep their type.
  const isReadOnly = (schema as any)?.readOnly === true;
  const formattedReadOnlyNumber = isNumeric && isReadOnly && typeof data === 'number';
  const fieldValue = formattedReadOnlyNumber
    ? formatNumber(
        data,
        schemaType === 'integer'
          ? { maximumFractionDigits: 0 }
          : { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      )
    : localValue;

  // After every hook: a field that toggles visibility must keep the same hook order.
  if (!visible) return null;

  return (
    <TextField
      value={fieldValue}
      onChange={handleValueChange}
      label={<FieldLabel label={label} description={description} />}
      required={required}
      disabled={!enabled}
      error={!!errors}
      helperText={errors}
      fullWidth
      size="small"
      type="text"
      multiline={isMultiline}
      // Rows the form declares are a fixed height (the text scrolls inside it — a stylesheet or a
      // message would otherwise grow the form to its full length); a long plain-text field grows.
      rows={options.multi === true ? (options.rows ?? 3) : undefined}
      minRows={isMultiline && options.multi !== true ? 3 : undefined}
      sx={
        contentFormat
          ? { '&:hover .nexo-content-view, & .nexo-content-view:focus-visible': { opacity: 1 } }
          : undefined
      }
      InputProps={
        contentFormat
          ? {
              endAdornment: (
                <ContentViewAdornment
                  option={contentFormat}
                  title={label}
                  value={typeof localValue === 'string' ? localValue : ''}
                  readOnly={!enabled || isReadOnly}
                  onChange={applyValue}
                />
              ),
            }
          : undefined
      }
      inputProps={{
        maxLength: (schema as any)?.maxLength || undefined,
        readOnly: (schema as any)?.readOnly || false,
        inputMode: isNumeric ? 'decimal' : undefined,
      }}
    />
  );
};

// Priority 3: above material defaults (1-2) but below enum (5), lookup (10), date (15)
const TOOLTIP_PRIORITY = 3;

// Exclude enums — let the Material enum renderer handle them at priority 5
const hasNoEnum = schemaMatches(
  (schema) => !schema.enum && !Array.isArray((schema as any)?.oneOf)
);

// --- Text (excludes enums) ---
export const tooltipTextRendererEntry = {
  tester: rankWith(TOOLTIP_PRIORITY, and(isStringControl, hasNoEnum)),
  renderer: withJsonFormsControlProps(TooltipTextField),
};

// --- Number ---
export const tooltipNumberRendererEntry = {
  tester: rankWith(TOOLTIP_PRIORITY, isNumberControl),
  renderer: withJsonFormsControlProps(TooltipTextField),
};

// --- Integer ---
export const tooltipIntegerRendererEntry = {
  tester: rankWith(TOOLTIP_PRIORITY, isIntegerControl),
  renderer: withJsonFormsControlProps(TooltipTextField),
};
